import { useEffect, useState } from 'react';

import { css } from '@emotion/react';

import { isServer } from 'src/utils/common';
import { API_IDENTIFICATION_CODE } from 'src/utils/constants/localStorageConstants';
import { NavMenuLinks } from 'src/utils/constants/RouterConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import { colors } from '../../styles/colors';
import { getData } from '../../utils/app-storage';
import { Fallback_Type_Constants } from '../../utils/constants/FallbackTypeConstants';
import { FOOTER_STORE_SOCIAL_LINKS } from '../../utils/constants/FooterConstants';
import { OtherIconsSet } from '../../utils/constants/IconsConstants';
import {
  amaFallBack,
  amaFallBackText
} from '../../utils/constants/stringConstants';
import {
  defaultFallbackContainerStyle,
  defaultFallbackDescriptionStyle,
  defaultFallbackIconStyle,
  defaultFallbackTitleStyle,
  fallbackContent,
  fallbackWrapper,
  noInternetDescriptionStyle,
  noInternetHeadingStyle
} from './FallbackStyles';

type FallbackProps = {
  type: keyof typeof Fallback_Type_Constants;
  retry?: () => void;
};

const fallbackApiErrorCode = css`
  display: flex;
  color: ${colors.white};
  align-items: center;
  flex-direction: column;
  position: relative;
  top: 12vh;
  font-size: 12px;
`;

const Fallback = (props: FallbackProps) => {
  const [apiIdentificationCode, setApiIdentificationCode] = useState('');
  const [gcpErrorMessage, setGcpErrorMessage] = useState('');

  const apiErrorResponse = useAppStore().apiErrorResponse;
  const { type, retry: retryHandler } = props;
  const fallbackStrings = useAppStore().translations?.['fallback'];

  const navigateHandler = (link: string) => {
    retryHandler && retryHandler();
    if (!isServer() && window?.location.pathname === NavMenuLinks.HOME) {
      window.location.reload();
    } else {
      //As of now, we are navigating to home page, which is not available as part of webapp, hence using widow.location.href
      window.location.href = link;
    }
  };
  const navigateToHomeHandler = () => {
    navigateHandler(NavMenuLinks.HOME);
  };

  const getErrorTitle = (type: string) => {
    switch (type) {
      case Fallback_Type_Constants.NOT_AVAILABLE:
        return fallbackStrings!.OOPS_NO_INFORMATION_AVAILABLE;
      case Fallback_Type_Constants.FEATURE_NOT_AVAILABLE:
        return amaFallBackText;
      case Fallback_Type_Constants.NO_ACTIVE_INTERNET:
        return fallbackStrings?.NO_INTERNET_CONNECTION_TITLE;
      case Fallback_Type_Constants.SOMETHING_WENT_WRONG:
        return fallbackStrings?.SOMETHING_WENT_WRONG;
      case Fallback_Type_Constants.CONTENT_NOT_AVAILABLE:
        return fallbackStrings!.CONTENT_NOT_AVAILABLE_IN_COUNTRY;
      case Fallback_Type_Constants.CONTENT_NOT_AVAILABLE_REGION:
        return fallbackStrings!.CONTENT_NOT_AVAILABLE_IN_REGION;
      case Fallback_Type_Constants.PAGE_NOT_FOUND:
      default:
        return fallbackStrings?.PAGE_NOT_FOUND;
    }
  };

  const NoInternetFallback = () => {
    return (
      <div style={{ marginTop: '250px' }}>
        <div css={defaultFallbackContainerStyle}>
          <div
            css={defaultFallbackIconStyle(
              OtherIconsSet.FALLBACK_PAGE_ICON,
              false
            )}
          />
          <div css={noInternetHeadingStyle}>
            {fallbackStrings?.NO_INTERNET_CONNECTION_TITLE}
          </div>
          <div css={noInternetDescriptionStyle}>
            {fallbackStrings?.NO_INTERNET_CONNECTION_DESCRIPTION}
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isServer() || apiErrorResponse) {
      setApiIdentificationCode(
        apiErrorResponse?.apiIdentificationCode as string
      );
      setGcpErrorMessage(apiErrorResponse?.message as string);
    } else {
      setApiIdentificationCode(getData(API_IDENTIFICATION_CODE));
    }
  });

  const DefaultFallback = () => {
    const errorIcon =
      type === Fallback_Type_Constants.SOMETHING_WENT_WRONG
        ? OtherIconsSet.SAD_ICON
        : OtherIconsSet.FALLBACK_PAGE_ICON;
    return (
      <div>
        <div
          css={defaultFallbackContainerStyle}
          data-test-id="defaultFallbackContainerStyle">
          <div
            data-test-id="defaultFallbackIconStyle"
            css={defaultFallbackIconStyle(
              errorIcon,
              type === Fallback_Type_Constants.FEATURE_NOT_AVAILABLE
            )}
          />
          <div
            data-test-id="defaultFallbackTitleStyle"
            css={defaultFallbackTitleStyle}>
            {gcpErrorMessage ? gcpErrorMessage : getErrorTitle(type)}
          </div>

          {type !== Fallback_Type_Constants.FEATURE_NOT_AVAILABLE ? (
            <div
              data-test-id="defaultFallbackDescriptionStyle"
              css={defaultFallbackDescriptionStyle}
              onClick={navigateToHomeHandler}>
              {fallbackStrings?.GO_HOME}
            </div>
          ) : (
            <a
              css={defaultFallbackDescriptionStyle}
              href={FOOTER_STORE_SOCIAL_LINKS.ANDROID}>
              {amaFallBack}
            </a>
          )}
        </div>
        <div css={fallbackApiErrorCode}>
          {apiIdentificationCode &&
            type !== Fallback_Type_Constants.FEATURE_NOT_AVAILABLE && (
              <span>Error Code: {apiIdentificationCode}</span>
            )}
        </div>
      </div>
    );
  };

  return (
    <div data-test-id="fallbackWrapper" css={fallbackWrapper}>
      <section data-test-id="fallbackContent" css={fallbackContent}>
        <DefaultFallback />
      </section>
    </div>
  );
};

export default Fallback;
